import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { FileText, Upload, Download, Filter, Archive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTeamPermissions } from '../contexts/TeamPermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Documents = () => {
  const { t } = useTranslation();
  const { canEdit } = useTeamPermissions();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [category, setCategory] = useState('');
  const [linkedPackageId, setLinkedPackageId] = useState('');
  const [linkedBookingId, setLinkedBookingId] = useState('');
  const [linkedExpenseId, setLinkedExpenseId] = useState('');
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPackage, setFilterPackage] = useState('');
  const [filterBooking, setFilterBooking] = useState('');
  const [filterExpense, setFilterExpense] = useState('');
  const [sortBy, setSortBy] = useState('uploaded_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [documents, filterCategory, filterPackage, filterBooking, filterExpense, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [docsRes, packagesRes, bookingsRes, expensesRes] = await Promise.all([
        axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/packages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/expenses`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setDocuments(docsRes.data);
      setPackages(packagesRes.data);
      setBookings(bookingsRes.data);
      setExpenses(expensesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...documents];

    // Apply filters
    if (filterCategory && filterCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category === filterCategory);
    }
    if (filterPackage && filterPackage !== 'all') {
      filtered = filtered.filter(doc => doc.metadata?.linked_package_id === filterPackage);
    }
    if (filterBooking && filterBooking !== 'all') {
      filtered = filtered.filter(doc => doc.metadata?.linked_booking_id === filterBooking);
    }
    if (filterExpense && filterExpense !== 'all') {
      filtered = filtered.filter(doc => doc.metadata?.linked_expense_id === filterExpense);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'filename':
          aVal = a.filename.toLowerCase();
          bVal = b.filename.toLowerCase();
          break;
        case 'category':
          aVal = a.category.toLowerCase();
          bVal = b.category.toLowerCase();
          break;
        case 'file_size':
          aVal = a.file_size;
          bVal = b.file_size;
          break;
        case 'uploaded_at':
        default:
          aVal = new Date(a.uploaded_at);
          bVal = new Date(b.uploaded_at);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredDocuments(filtered);
  };

  const getFilteredBookings = () => {
    if (!linkedPackageId) return bookings;
    return bookings.filter(b => b.package_id === linkedPackageId);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', category);
      if (linkedPackageId) {
        formData.append('linked_package_id', linkedPackageId);
      }
      if (linkedBookingId) {
        formData.append('linked_booking_id', linkedBookingId);
      }
      if (linkedExpenseId) {
        formData.append('linked_expense_id', linkedExpenseId);
      }

      await axios.post(`${API}/documents`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Document uploaded successfully!');
      setDialogOpen(false);
      setUploadFile(null);
      setCategory('');
      setLinkedPackageId('');
      setLinkedBookingId('');
      setLinkedExpenseId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const byteCharacters = atob(response.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.file_type });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded!');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleArchiveDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(`${API}/documents/${documentId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive document');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/documents/${documentId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document moved to trash');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const clearFilters = () => {
    setFilterCategory('');
    setFilterPackage('');
    setFilterBooking('');
    setFilterExpense('');
  };

  if (loading) {
    return <div className="text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-8" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-extrabold text-foreground tracking-tight">{t('documents.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('documents.uploadAndOrganize')}</p>
        </div>
        {canEdit('documents') && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-document-button" className="bg-primary text-primary-foreground hover:bg-primary/90 transition-all active:scale-95">
                <Upload className="w-4 h-4 mr-2" />
                {t('documents.uploadDocument')}
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('documents.uploadDocument')}</DialogTitle>
              <DialogDescription>{t('documents.subtitle')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="file">{t('documents.selectFile')}</Label>
                <Input
                  id="file"
                  data-testid="file-input"
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">{t('documents.documentType')}</Label>
                <Select value={category} onValueChange={setCategory} required>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={t('documents.selectType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Client Sales Invoice">{t('documents.clientSalesInvoice')}</SelectItem>
                    <SelectItem value="Booking Acknowledgement">{t('documents.bookingAcknowledgement')}</SelectItem>
                    <SelectItem value="Supplier Invoice">{t('documents.supplierInvoice')}</SelectItem>
                    <SelectItem value="Payment Receipt">{t('documents.paymentReceipt')}</SelectItem>
                    <SelectItem value="Other Payment Receipt">{t('documents.otherPaymentReceipt')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="linked_package_id">{t('documents.linkToPackage')}</Label>
                <Select 
                  value={linkedPackageId || undefined} 
                  onValueChange={(value) => {
                    setLinkedPackageId(value === "none" ? "" : value);
                    setLinkedBookingId("");
                  }}
                >
                  <SelectTrigger data-testid="package-id-select">
                    <SelectValue placeholder={t('documents.selectPackage')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('documents.notLinked')}</SelectItem>
                    {packages.map((pkg) => (
                      <SelectItem key={pkg.package_id} value={pkg.package_id}>
                        {pkg.package_id} - {pkg.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="linked_booking_id">{t('documents.linkToBooking')}</Label>
                <Select 
                  value={linkedBookingId || undefined} 
                  onValueChange={(value) => setLinkedBookingId(value === "none" ? "" : value)}
                  disabled={!linkedPackageId}
                >
                  <SelectTrigger data-testid="booking-id-select">
                    <SelectValue placeholder={linkedPackageId ? t('documents.selectBooking') : t('documents.selectPackage')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('documents.notLinked')}</SelectItem>
                    {getFilteredBookings().map((booking) => (
                      <SelectItem key={booking.booking_id} value={booking.booking_id}>
                        {booking.booking_id} - {booking.service_provider}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="linked_expense_id">{t('documents.linkToExpense')}</Label>
                <Select 
                  value={linkedExpenseId || undefined} 
                  onValueChange={(value) => setLinkedExpenseId(value === "none" ? "" : value)}
                >
                  <SelectTrigger data-testid="expense-id-select">
                    <SelectValue placeholder={t('documents.selectExpense')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('documents.notLinked')}</SelectItem>
                    {expenses.map((expense) => (
                      <SelectItem key={expense.expense_id} value={expense.expense_id}>
                        <div className="flex flex-col">
                          <span className="font-mono text-xs">{expense.expense_id}</span>
                          <span className="text-xs text-muted-foreground truncate max-w-[250px]">
                            {expense.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-upload-button">
                {t('documents.upload')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-heading font-semibold flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {t('documents.filtersAndSorting')}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              {t('documents.clearFilters')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label>{t('expenses.category')}</Label>
              <Select value={filterCategory || undefined} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue placeholder={t('documents.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="Client Sales Invoice">{t('documents.clientSalesInvoice')}</SelectItem>
                  <SelectItem value="Booking Acknowledgement">{t('documents.bookingAcknowledgement')}</SelectItem>
                  <SelectItem value="Supplier Invoice">{t('documents.supplierInvoice')}</SelectItem>
                  <SelectItem value="Payment Receipt">{t('documents.paymentReceipt')}</SelectItem>
                  <SelectItem value="Other Payment Receipt">{t('documents.otherPaymentReceipt')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('packages.title')}</Label>
              <Select value={filterPackage || undefined} onValueChange={setFilterPackage}>
                <SelectTrigger>
                  <SelectValue placeholder={t('documents.allPackages')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {packages.map((pkg) => (
                    <SelectItem key={pkg.package_id} value={pkg.package_id}>
                      {pkg.package_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('bookings.title')}</Label>
              <Select value={filterBooking || undefined} onValueChange={setFilterBooking}>
                <SelectTrigger>
                  <SelectValue placeholder={t('documents.allBookings')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {bookings.map((booking) => (
                    <SelectItem key={booking.booking_id} value={booking.booking_id}>
                      {booking.booking_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('expenses.title')}</Label>
              <Select value={filterExpense || undefined} onValueChange={setFilterExpense}>
                <SelectTrigger>
                  <SelectValue placeholder={t('documents.allExpenses')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {expenses.map((expense) => (
                    <SelectItem key={expense.expense_id} value={expense.expense_id}>
                      {expense.expense_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('documents.sortBy')}</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="uploaded_at">{t('documents.uploadDate')}</SelectItem>
                  <SelectItem value="filename">{t('documents.fileName')}</SelectItem>
                  <SelectItem value="category">{t('expenses.category')}</SelectItem>
                  <SelectItem value="file_size">{t('documents.fileSize')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('documents.order')}</Label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">{t('documents.descending')}</SelectItem>
                  <SelectItem value="asc">{t('documents.ascending')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-heading font-semibold">
            {t('documents.allDocuments')} ({filteredDocuments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredDocuments.length > 0 ? (
            <div className="w-full overflow-auto rounded-lg border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('documents.documentId')}</TableHead>
                    <TableHead className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('documents.fileName')}</TableHead>
                    <TableHead className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('documents.documentType')}</TableHead>
                    <TableHead className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('packages.packageId')}</TableHead>
                    <TableHead className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('bookings.bookingId')}</TableHead>
                    <TableHead className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('expenses.expenseId')}</TableHead>
                    <TableHead className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('documents.fileSize')}</TableHead>
                    <TableHead className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('documents.uploadDate')}</TableHead>
                    <TableHead className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.document_id} className="border-b border-border/50 hover:bg-muted/30 transition-colors" data-testid="document-row">
                      <TableCell className="p-4 align-middle text-sm font-mono">{doc.document_id}</TableCell>
                      <TableCell className="p-4 align-middle text-sm max-w-xs truncate" title={doc.filename}>{doc.filename}</TableCell>
                      <TableCell className="p-4 align-middle text-sm">
                        <Badge variant="secondary">{doc.category}</Badge>
                      </TableCell>
                      <TableCell className="p-4 align-middle text-sm font-mono">
                        {doc.metadata?.linked_package_id || '-'}
                      </TableCell>
                      <TableCell className="p-4 align-middle text-sm font-mono">
                        {doc.metadata?.linked_booking_id || '-'}
                      </TableCell>
                      <TableCell className="p-4 align-middle text-sm font-mono">
                        {doc.metadata?.linked_expense_id || '-'}
                      </TableCell>
                      <TableCell className="p-4 align-middle text-sm font-mono text-right">
                        {(doc.file_size / 1024).toFixed(2)} KB
                      </TableCell>
                      <TableCell className="p-4 align-middle text-sm">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="p-4 align-middle text-sm text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(doc.document_id, doc.filename)}
                            data-testid="download-button"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {t('common.download')}
                          </Button>
                          {canEdit('documents') && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleArchiveDocument(doc.document_id)}
                                title={t('common.archive')}
                              >
                                <Archive className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDocument(doc.document_id)}
                                title={t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-documents-message">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('documents.noDocuments')}</p>
              <p className="text-sm mt-2">
                {documents.length > 0 ? t('common.tryAdjustingFilters') : t('documents.uploadFirst')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Documents;
